import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { HERO_VIDEO, HERO_POSTER } from '@/data/home';
import { homeT } from '@/i18n/homeI18n';

export function HeroSection({ lang = 'en' }) {
  const t = homeT(lang).hero;
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#121212] text-white">
      <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline poster={HERO_POSTER} aria-hidden="true">
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-black/80 via-black/50 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-32 min-h-[560px] lg:min-h-[640px] flex flex-col justify-center">
        <p data-testid="hero-heritage" className="inline-flex w-fit flex-wrap items-center gap-x-3 gap-y-1 font-heading font-bold text-[11px] tracking-[0.25em] uppercase text-white/85 animate-fade-in">
          <span className="h-px w-8 bg-[#EE5A01]" aria-hidden="true" /><span className="whitespace-nowrap">{t.est}</span><span className="text-[#EE5A01]">·</span><span>{t.heritage}</span>
        </p>
        <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-white max-w-2xl animate-fade-in-up">{t.title}</h1>
        <p className="mt-5 font-body text-base sm:text-lg text-white/75 max-w-lg leading-relaxed animate-fade-in" style={{ animationDelay: '0.15s' }}>{t.lead}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <Link to={t.personalHref} data-testid="hero-cta-personal" className="btn-primary">{t.personal} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
          <Link to={t.businessHref} data-testid="hero-cta-business" className="btn-outline !border-white/70 !text-white hover:!bg-white hover:!text-[#121212]">{t.business}</Link>
        </div>
        <a href="https://www.youtube.com/@eurogulfmobility" target="_blank" rel="noopener noreferrer" className="group mt-10 hidden sm:inline-flex items-center gap-3 text-white/70 hover:text-white font-heading font-semibold text-xs w-fit transition-colors">
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/40 group-hover:border-white transition-colors"><Play className="w-4 h-4 fill-current rtl:rotate-180" /></span>
          {t.film}
        </a>
      </div>
    </section>
  );
}

export function StatsBand({ lang = 'en' }) {
  const stats = homeT(lang).stats;
  return (
    <div data-testid="stats-band" className="bg-white border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/5">
          {stats.map((s, i) => (
            <div key={s.label} data-testid={`stat-${i}`} className="bg-white py-7 lg:py-8 px-5 lg:px-8">
              <p className="font-heading font-black text-2xl lg:text-3xl text-[#121212] leading-none tracking-tight">
                {s.value}{s.suffix && <span className="ms-1 text-sm font-bold text-[#EE5A01] uppercase tracking-wider align-middle">{s.suffix}</span>}
              </p>
              <p className="mt-2 font-body text-xs lg:text-sm text-[#666666]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
